import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { AppThunk } from '../store';
import type { CreateDeliveryLocationParams, DeleteDeliveryLocationParams, DeliveryLocationType, DeliveryLocationState, ModalMode, UpdateDeliveryLocationParams } from '../../types/deliveryLocation';
import deliveryLocationAPI from '../../api/deliveryLocationAPI';

export const initialState: DeliveryLocationState = {
  isLoading: false,
  hasError: false,
  serverMessage: undefined,
  dialogMode: undefined,
  isDialogOpen: false,
  deliveryLocation: undefined,
  deliveryLocations: undefined,
};

const slice = createSlice({
  name: 'DeliveryLocation',
  initialState,
  reducers: {
    startLoading(state: DeliveryLocationState) {
      state.isLoading = true;
    },

    hasError(state: DeliveryLocationState, action: PayloadAction<string>) {
      // console.log(action.payload.response);
      const message = action.payload;
      state.isLoading = false;
      state.hasError = true;
      state.serverMessage = message;
    },

    resetMessage(state: DeliveryLocationState) {
      state.isLoading = false;
      state.serverMessage = undefined;
      state.hasError = false;
    },

    setServerMessage(state: DeliveryLocationState, action: PayloadAction<string>): void {
      state.isLoading = false;
      state.serverMessage = action.payload;
    },

    setDialogMode(state: DeliveryLocationState, action: PayloadAction<string>): void {
      state.dialogMode = action.payload;
    },

    openDialog(state: DeliveryLocationState): void {
      state.isDialogOpen = true;
    },

    closeDialog(state: DeliveryLocationState): void {
      state.isDialogOpen = false;
      state.dialogMode = undefined;
      state.deliveryLocation = undefined;
    },

    selectDeliveryLocation(state: DeliveryLocationState, action: PayloadAction<DeliveryLocationType>): void {
      if (action.payload !== undefined) {
        const deliveryLocation: DeliveryLocationType = action.payload;
        const selectedDeliveryLocation: DeliveryLocationType | undefined = state.deliveryLocations.find((location) => location.code === deliveryLocation.code);
        if (selectedDeliveryLocation !== undefined) {
          state.deliveryLocation = selectedDeliveryLocation;
        } else {
          state.deliveryLocation = action.payload;
        }
      } else {
        state.deliveryLocation = action.payload;
      }
    },
  },

  extraReducers: (builder) => {
    builder
      // Get DeliveryLocations
      // ----------------------------------------
      .addCase(getDeliveryLocations.pending, (state, action) => {
        state.isLoading = true;
      })
      .addCase(getDeliveryLocations.fulfilled, (state, action) => {
        state.isLoading = false;
        state.deliveryLocations = action.payload;
        // const deliveryLocations = action.payload;
        // state.deliveryLocations = deliveryLocations.sort((a, b) => {
        //   // https://developer.mozilla.org/ja/docs/Web/JavaScript/Reference/Global_Objects/Array/sort
        //   const nameA = a.code.toUpperCase(); // 大文字と小文字を無視する
        //   const nameB = b.code.toUpperCase(); // 大文字と小文字を無視する
        //   if (nameA < nameB) {
        //     return -1;
        //   }
        //   if (nameA > nameB) {
        //     return 1;
        //   }
        //   // names must be equal
        //   return 0;
        // });
      })
      .addCase(getDeliveryLocations.rejected, (state, action) => {
        const message = action.error.message;
        state.isLoading = false;
        state.hasError = true;
        state.serverMessage = message;
        state.deliveryLocations = state.deliveryLocations ? state.deliveryLocations : [];
      })
      // Create DeliveryLocation
      // ----------------------------------------
      .addCase(createDeliveryLocation.pending, (state, action) => {
        state.isLoading = true;
      })
      .addCase(createDeliveryLocation.fulfilled, (state, action) => {
        state.isLoading = false;
        const createdDeliveryLocation = action.payload;
        state.deliveryLocation = undefined;
        state.deliveryLocations = state.deliveryLocations ? [...state.deliveryLocations, createdDeliveryLocation] : [createdDeliveryLocation];
      })
      .addCase(createDeliveryLocation.rejected, (state, action) => {
        const message = action.error.message;
        state.isLoading = false;
        state.hasError = true;
        state.serverMessage = message;
        state.deliveryLocation = undefined;
        state.deliveryLocations = state.deliveryLocations ? state.deliveryLocations : [];
      })
      // Update DeliveryLocation
      // ----------------------------------------
      .addCase(updateDeliveryLocation.pending, (state, action) => {
        state.isLoading = true;
      })
      .addCase(updateDeliveryLocation.fulfilled, (state, action) => {
        state.isLoading = false;
        const updatedDeliveryLocation = action.payload;
        state.deliveryLocation = undefined;
        state.deliveryLocations = state.deliveryLocations.map((deliveryLocation) => {
          if (deliveryLocation.id === updatedDeliveryLocation.id) {
            return updatedDeliveryLocation;
          } else {
            return deliveryLocation;
          }
        });
      })
      .addCase(updateDeliveryLocation.rejected, (state, action) => {
        const message = action.error.message;
        state.isLoading = false;
        state.hasError = true;
        state.serverMessage = message;
        state.deliveryLocation = undefined;
        state.deliveryLocations = state.deliveryLocations ? state.deliveryLocations : [];
      })
      // Delete DeliveryLocation
      // ----------------------------------------
      .addCase(deleteDeliveryLocation.pending, (state, action) => {
        state.isLoading = true;
      })
      .addCase(deleteDeliveryLocation.fulfilled, (state, action) => {
        state.isLoading = false;
        const deletedDeliveryLocation = action.payload;
        state.deliveryLocation = undefined;
        state.deliveryLocations = state.deliveryLocations.filter((location) => {
          if (location.id === deletedDeliveryLocation.id) {
            return false;
          } else {
            return true;
          }
        });
      })
      .addCase(deleteDeliveryLocation.rejected, (state, action) => {
        const message = action.error.message;
        state.isLoading = false;
        state.hasError = true;
        state.serverMessage = message;
        state.deliveryLocation = undefined;
        state.deliveryLocations = state.deliveryLocations ? state.deliveryLocations : [];
      });
  },
});

export const { reducer, actions } = slice;

export const resetMessage =
  (): AppThunk =>
  (dispatch): void => {
    dispatch(slice.actions.resetMessage());
  };

export const setDialogMode =
  (mode?: ModalMode): AppThunk =>
  (dispatch): void => {
    dispatch(slice.actions.setDialogMode(mode));
  };

export const openDialog =
  (): AppThunk =>
  (dispatch): void => {
    dispatch(slice.actions.openDialog());
  };

export const closeDialog =
  (): AppThunk =>
  (dispatch): void => {
    dispatch(slice.actions.closeDialog());
  };

export const selectDeliveryLocation =
  (deliveryLocation?: DeliveryLocationType): AppThunk =>
  (dispatch): void => {
    dispatch(slice.actions.selectDeliveryLocation(deliveryLocation));
  };

export const getDeliveryLocations = createAsyncThunk('ManageDeliveryLocation/getDeliveryLocations', async () => {
  const response = await deliveryLocationAPI.getDeliveryLocations();
  return response.data;
});

export const createDeliveryLocation = createAsyncThunk('ManageDeliveryLocation/createDeliveryLocation', async (params: CreateDeliveryLocationParams) => {
  const response = await deliveryLocationAPI.createDeliveryLocation(params);
  return response.data;
});

export const updateDeliveryLocation = createAsyncThunk('ManageDeliveryLocation/updateDeliveryLocation', async (params: UpdateDeliveryLocationParams) => {
  const response = await deliveryLocationAPI.updateDeliveryLocation(params);
  return response.data;
});

export const deleteDeliveryLocation = createAsyncThunk('ManageDeliveryLocation/deleteDeliveryLocation', async (params: DeleteDeliveryLocationParams) => {
  const response = await deliveryLocationAPI.deleteDeliveryLocation(params);
  return response.data;
});

export default slice;
